// User Management Component - Only visible to Admin/Manager users
import React, { useState, useEffect } from 'react';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import PermissionGate from '../PermissionGate/PermissionGate';
import './UserManagement.css';

const UserManagement = () => {
  const { permissions } = useUserPermissions();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // TEMPORARILY BYPASS PERMISSION CHECK - EVERYONE CAN SEE USER MANAGEMENT
    // TODO: Remove this bypass when permission gates need to be re-enabled
    loadUsers();
    
    // Original permission check (commented out for temporary bypass)
    /*
    if (permissions?.canSeeUserManagement) {
      loadUsers();
    }
    */
  }, [permissions]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Use the global function we created
      const userList = await window.getUserList();
      setUsers(userList.users || []);
    } catch (err) {
      setError(err.message);
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignAdmin = async (username) => {
    try {
      await window.assignUserToAdminGroup(username);
      alert(`User ${username} has been assigned Admin privileges`);
      loadUsers(); // Refresh the list
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleRemoveAdmin = async (username) => {
    try {
      await window.removeUserFromAdminGroup(username);
      alert(`User ${username} has been removed from Admin group`);
      loadUsers(); // Refresh the list
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleDeleteUser = async (username) => {
    if (window.confirm(`Are you sure you want to delete user ${username}?`)) {
      try {
        await window.removeUser(username);
        alert(`User ${username} has been deleted`);
        loadUsers(); // Refresh the list
      } catch (err) {
        alert(`Error: ${err.message}`);
      }
    }
  };

  // TEMPORARILY BYPASS PERMISSION CHECK - EVERYONE CAN SEE USER MANAGEMENT
  // TODO: Remove this bypass when permission gates need to be re-enabled
  /*
  if (!permissions?.canSeeUserManagement) {
    return (
      <div className="user-management">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to access user management.</p>
        </div>
      </div>
    );
  }
  */

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h1>User Management</h1>
        <button onClick={loadUsers} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh Users'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="user-list">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Groups</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user, index) => (
              <tr key={index}>
                <td>{user.email}</td>
                <td>
                  <div className="groups">
                    {user.groups.map((group, idx) => (
                      <span key={idx} className={`group-badge ${group.toLowerCase()}`}>
                        {group}
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  <span className={`status ${user.status.toLowerCase()}`}>
                    {user.status}
                  </span>
                </td>
                <td>
                  <div className="actions">
                    {/* Only admins can assign/remove admin privileges */}
                    <PermissionGate permission="assign_admin">
                      {user.groups.includes('Admin') ? (
                        <button 
                          onClick={() => handleRemoveAdmin(user.username)}
                          className="btn-remove-admin"
                        >
                          Remove Admin
                        </button>
                      ) : (
                        <button 
                          onClick={() => handleAssignAdmin(user.username)}
                          className="btn-assign-admin"
                        >
                          Make Admin
                        </button>
                      )}
                    </PermissionGate>
                    
                    {/* Only admins can delete users */}
                    <PermissionGate permission="delete_users">
                      <button 
                        onClick={() => handleDeleteUser(user.username)}
                        className="btn-delete"
                      >
                        Delete
                      </button>
                    </PermissionGate>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManagement;
